import {
  flip,
  hide,
  offset,
  ReferenceType,
  useFloating,
} from '@floating-ui/react';
import {
  getSelectionText,
  useEventEditorSelectors,
} from 'lib-knowledge-document-structure/plate/common';
import { useMyEditorState } from 'lib-knowledge-document-structure/plateHooks';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  FloatingProps,
  getSelectionBoundingClientRect,
  useToggleAutoAttach,
} from './hoverUtils';

type Options = { forcedClosed?: boolean };

/**
 * Helps position an floating element near the editor selection.
 *
 * @returns
 */
const useSelectionHover = (
  floatingDivId: string,
  { forcedClosed }: Options = {},
): FloatingProps => {
  const editor = useMyEditorState();
  const [isOpen, setIsOpen] = useState(false);
  const virtualElementRef = useRef<ReferenceType>({
    getBoundingClientRect: getSelectionBoundingClientRect,
  });
  const focusedEditorId = useEventEditorSelectors.focus();
  const selectionText = editor && getSelectionText(editor);
  const focused = editor.id === focusedEditorId;

  useEffect(
    function hideOrShowAndUpdate() {
      if (!focused || !selectionText || forcedClosed) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    },
    [selectionText, focused, forcedClosed],
  );

  const floatingResult = useFloating({
    middleware: [
      offset(12),
      flip({
        padding: 150,
      }),
      hide({
        padding: {
          top: 64 + 48, // Size of the two heading elements
          left: 0,
          bottom: 0,
          right: 0,
        },
      }),
    ],
    placement: 'top',
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: 'fixed',
  });

  const { refs, middlewareData, update } = floatingResult;

  // We're not using a real element, so update floating-ui's virtual one.
  useLayoutEffect(() => {
    refs.setReference(virtualElementRef.current);
  }, [refs]);

  useToggleAutoAttach({
    isOpen,
    update,
    floatingDivId,
    virtualElementRef: virtualElementRef.current,
  });

  return {
    ...floatingResult,
    isOpen,
    hide: middlewareData.hide?.referenceHidden || false,
  };
};

export default useSelectionHover;
